import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient, type ImportKind } from '@/lib/api-client';

interface ImportUploadProps {
  kind: ImportKind;
  onSuccess: (jobId: string) => void;
}

export function ImportUpload({ kind, onSuccess }: ImportUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickFile(f: File | null) {
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      setError('Apenas arquivos .xlsx ou .xls são aceitos.');
      return;
    }
    setError(null);
    setFile(f);
  }

  async function handleSubmit() {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const job = await apiClient.importsCreate(fd);
      const parsed = await apiClient.importsParse(job.id);
      onSuccess(parsed.id);
    } catch (e) {
      const msg = (e as { message?: string }).message ?? 'Erro no upload';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
          {error}
        </p>
      )}

      <div
        role="button"
        tabIndex={0}
        aria-label="Área de upload"
        className={[
          'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 transition cursor-pointer select-none',
          dragging
            ? 'border-[var(--accent)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]'
            : 'border-[var(--border)] hover:border-[var(--accent)] hover:bg-[color-mix(in_oklch,var(--accent)_4%,transparent)]',
        ].join(' ')}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) pickFile(f);
        }}
      >
        <Upload className="h-8 w-8 text-[var(--fg-muted)]" />
        <div className="text-center">
          <p className="text-sm font-medium text-[var(--fg)]">
            {file ? file.name : 'Arraste o arquivo ou clique para selecionar'}
          </p>
          <p className="mt-1 text-xs text-[var(--fg-muted)]">
            Formato aceito: .xlsx ou .xls (exportado do ADVBOX)
          </p>
        </div>
        {file && (
          <span className="rounded-full bg-emerald-500/15 px-3 py-0.5 text-xs font-medium text-emerald-500">
            {(file.size / 1024).toFixed(0)} KB selecionado
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
      />

      <div className="flex justify-end gap-2">
        <Button
          onClick={handleSubmit}
          disabled={!file || loading}
        >
          {loading ? 'Processando…' : 'Enviar e processar'}
        </Button>
      </div>
    </div>
  );
}
